import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Editor, OnMount } from '@monaco-editor/react';
import Subheading from '../components/font/Subheading';
import Modal from '../components/overlay/Modal';
import { useToast } from '../components/toast/ToastProvider';
import { isGraphqlMutation, ShopifyGraphqlResult } from '../models/shopify';
import { runShopifyGraphql } from '../requests/shopify.request';

const QUERY_STORAGE_KEY = 'CS_ADMIN_SHOPIFY_GRAPHQL_QUERY';
const VARIABLES_STORAGE_KEY = 'CS_ADMIN_SHOPIFY_GRAPHQL_VARIABLES';
const DEFAULT_QUERY = `{
  shop {
    name
    myshopifyDomain
    plan { publicDisplayName }
  }
}
`;

// The draft survives navigation; blocked storage just means it doesn't.
const readStored = (key: string, fallback: string) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const store = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore blocked or full storage.
  }
};

const errorMessage = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof message === 'string' ? message : fallback;
};

const parseVariables = (text: string): Record<string, unknown> | null => {
  if (!text.trim()) return null;
  const value = JSON.parse(text);
  if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new Error('Variables must be a JSON object.');
  return value;
};

type Cost = { requestedQueryCost?: number; actualQueryCost?: number; throttleStatus?: { currentlyAvailable?: number; maximumAvailable?: number } };

const costText = (response: unknown) => {
  const cost = (response as { extensions?: { cost?: Cost } } | null)?.extensions?.cost;
  if (!cost) return null;
  const used = cost.actualQueryCost ?? cost.requestedQueryCost;
  const throttle = cost.throttleStatus;
  return `Cost ${used ?? '—'}${throttle ? ` · ${throttle.currentlyAvailable} / ${throttle.maximumAvailable} available` : ''}`;
};

const EDITOR_OPTIONS = { minimap: { enabled: false }, scrollBeyondLastLine: false, tabSize: 2, automaticLayout: true };

const ShopifyGraphql = () => {
  const { id } = useParams();
  const toast = useToast();
  const [query, setQuery] = useState(() => readStored(QUERY_STORAGE_KEY, DEFAULT_QUERY));
  const [variables, setVariables] = useState(() => readStored(VARIABLES_STORAGE_KEY, ''));
  const [result, setResult] = useState<ShopifyGraphqlResult | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [confirmMutation, setConfirmMutation] = useState(false);

  useEffect(() => store(QUERY_STORAGE_KEY, query), [query]);
  useEffect(() => store(VARIABLES_STORAGE_KEY, variables), [variables]);

  // Results belong to the shop they ran against.
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [id]);

  const run = async (confirmed = false) => {
    if (!id || !query.trim() || running) return;
    let parsed: Record<string, unknown> | null;
    try {
      parsed = parseVariables(variables);
    } catch (parseError) {
      setError(`Invalid variables: ${(parseError as Error).message}`);
      return;
    }
    if (!confirmed && isGraphqlMutation(query)) {
      setConfirmMutation(true);
      return;
    }
    setConfirmMutation(false);
    setRunning(true);
    setError(null);
    const started = performance.now();
    try {
      const response = await runShopifyGraphql(id, query, parsed);
      setResult(response);
      setDuration(Math.round(performance.now() - started));
      if (response.mutation) toast.show('Mutation sent to Shopify');
    } catch (runError) {
      setResult(null);
      setError(errorMessage(runError, 'Could not run the query.'));
    } finally {
      setRunning(false);
    }
  };

  // Monaco keeps the command from its first mount, so it calls the latest run through a ref.
  const runRef = useRef(run);
  runRef.current = run;
  const addRunShortcut: OnMount = (editor, monaco) => {
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runRef.current());
  };

  const responseText = result ? JSON.stringify(result.response, null, 2) : '';
  const failed = result && (result.status >= 400 || !!(result.response as { errors?: unknown } | null)?.errors);
  const cost = result && costText(result.response);

  return (
    <Container>
      <Header>
        <s-heading fontSize='large'>Shopify GraphQL</s-heading>
        <s-button variant='primary' icon='play' onClick={() => run()} loading={running} disabled={!query.trim()}>
          Run
        </s-button>
      </Header>
      <Muted>
        Runs against the shop's Admin GraphQL API with its access token. Press ⌘/Ctrl + Enter in an editor to run.
        Mutations change the live shop and are recorded in History.
      </Muted>
      {error && <s-banner tone='critical'><p>{error}</p></s-banner>}
      <Columns>
        <Column>
          <Subheading>Query</Subheading>
          <EditorFrame $height={360}>
            <Editor value={query} language='graphql' height='100%' options={EDITOR_OPTIONS}
              wrapperProps={{ 'aria-label': 'GraphQL query' }} onMount={addRunShortcut}
              onChange={value => setQuery(value ?? '')} />
          </EditorFrame>
          <Subheading>Variables (JSON)</Subheading>
          <EditorFrame $height={140}>
            <Editor value={variables} language='json' height='100%'
              options={{ ...EDITOR_OPTIONS, lineNumbers: 'off' }}
              wrapperProps={{ 'aria-label': 'GraphQL variables' }} onMount={addRunShortcut}
              onChange={value => setVariables(value ?? '')} />
          </EditorFrame>
        </Column>
        <Column>
          <ResponseHeader>
            <Subheading>Response</Subheading>
            {result && (
              <s-stack direction='inline' gap='small-200' alignItems='center'>
                <s-badge tone={failed ? 'critical' : 'success'}>{`HTTP ${result.status}`}</s-badge>
                {result.mutation && <s-badge tone='caution'>Mutation</s-badge>}
                <Muted>{[duration !== null ? `${duration} ms` : null, cost].filter(Boolean).join(' · ')}</Muted>
              </s-stack>
            )}
          </ResponseHeader>
          <EditorFrame $height={540}>
            {running && !result ? (
              <Loading><s-spinner size='large' accessibilityLabel='Running query' /></Loading>
            ) : (
              <Editor value={responseText} language='json' height='100%'
                options={{ ...EDITOR_OPTIONS, readOnly: true, domReadOnly: true, folding: true }}
                wrapperProps={{ 'aria-label': 'GraphQL response' }} />
            )}
          </EditorFrame>
        </Column>
      </Columns>

      <Modal
        open={confirmMutation}
        onClose={() => setConfirmMutation(false)}
        heading='Run mutation?'
        primaryAction={{ content: 'Run mutation', destructive: true, loading: running, onAction: () => run(true) }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setConfirmMutation(false) }]}
      >
        <p>
          This query contains a mutation, which changes the live Shopify shop. It is recorded in History
          but can't be undone from there.
        </p>
      </Modal>
    </Container>
  );
};

export default ShopifyGraphql;

const Container = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ResponseHeader = styled(Header)`
  min-height: 24px;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
  gap: 16px;
  min-width: 0;
`;

const Column = styled.div`
  display: grid;
  align-content: start;
  gap: 8px;
  min-width: 0;
`;

const EditorFrame = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  min-width: 0;
  padding: 8px 0;
  border: 1px solid #d2d5d8;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`;

const Loading = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const Muted = styled.p`
  margin: 0;
  color: #6d7175;
`;
